/**
 * Future Me Mail Club — Production landing page
 *
 * SHOPIFY SETUP
 * -------------
 * 1. Paste the final Shopify checkout/product URL for each plan below.
 * 2. Alternatively, for one-time prepaid products, add a Shopify variant ID and
 *    this script can build a cart permalink automatically.
 * 3. For a recurring monthly subscription that uses a Shopify selling plan,
 *    use the direct checkout/product URL produced by your subscription setup.
 *
 * PURCHASE TRACKING
 * -----------------
 * This landing-page script tracks discovery, plan selection, and checkout intent.
 * Track completed purchases in Shopify Customer Events (or Shopify's Meta app pixel)
 * using Shopify's checkout_completed event. Do not fire Purchase from this page,
 * because the purchase happens after the visitor leaves for Shopify checkout.
 */

(() => {
  "use strict";

  /* ==========================================================================
     Configuration
     ========================================================================== */

  const CONFIG = Object.freeze({
    firstMailDate: "2026-10-01T09:00:00-04:00",

    shopify: {
      storeUrl: "https://amaryacademy.com",
      sourceRef: "future-me-mail-club",

      plans: {
        // https://amaryacademy.com/products/the-mail-club-12-month.js
          monthly: {
            name: "Monthly — Ages 6–10",
            price: 12,
            currency: "USD",
            checkoutUrl: "",
            variantId: "43664498032753",
            sellingPlanId: "3203072113",
            discountCode: ""
          },

          six_month: {
            name: "6-Month Adventure — Ages 6–10",
            price: 65,
            currency: "USD",
            checkoutUrl: "",
            variantId: "43664499048561",
            sellingPlanId: "3203104881",
            discountCode: ""
          },

          twelve_month: {
            name: "12-Month Dreamer — Ages 6–10",
            price: 119,
            currency: "USD",
            checkoutUrl: "",
            variantId: "43664499212401",
            sellingPlanId: "3203137649", 
            discountCode: ""
          },

          monthly_explorer: {
            name: "Monthly Explorer — Ages 3–5",
            price: 12,
            currency: "USD",
            checkoutUrl: "",
            variantId: "43911414349937",
            sellingPlanId: "3203072113",
            discountCode: ""
          },

          six_month_explorer: {
            name: "6-Month Explorer — Ages 3–5",
            price: 65,
            currency: "USD",
            checkoutUrl: "",
            variantId: "43911414284401",
            sellingPlanId: "3203104881",
            discountCode: ""
          },

          twelve_month_explorer: {
            name: "12-Month Explorer — Ages 3–5",
            price: 119,
            currency: "USD",
            checkoutUrl: "",
            variantId: "43911414251633",
            sellingPlanId: "3203137649",
            discountCode: ""
          }
        }
      },

    planSelector: {
      defaultPlan: "twelve_month"
    },

    mobileCta: {
      showAfterPx: 700
    }
  });

  const ATTRIBUTION_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "fbclid",
    "gclid",
    "ttclid",
    "msclkid"
  ];

  const STORAGE_KEYS = Object.freeze({
    firstTouch: "future_me_first_touch",
    lastTouch: "future_me_last_touch"
  });

  const state = {
    selectedPlan: CONFIG.planSelector.defaultPlan,
    modalSource: "unknown",
    lastFocusedElement: null,
    toastTimer: null,
    pricingInView: false
  };

  const PLAN_SELECTIONS = Object.freeze({
    monthly: {
      edition: "elementary",
      term: "monthly",
      editionLabel: "Ages 6–10",
      termLabel: "Monthly"
    },

    six_month: {
      edition: "elementary",
      term: "six_month",
      editionLabel: "Ages 6–10",
      termLabel: "6 Months"
    },

    twelve_month: {
      edition: "elementary",
      term: "twelve_month",
      editionLabel: "Ages 6–10",
      termLabel: "12 Months"
    },

    monthly_explorer: {
      edition: "explorer",
      term: "monthly",
      editionLabel: "Explorer Ages 3–5",
      termLabel: "Monthly"
    },

    six_month_explorer: {
      edition: "explorer",
      term: "six_month",
      editionLabel: "Explorer Ages 3–5",
      termLabel: "6 Months"
    },

    twelve_month_explorer: {
      edition: "explorer",
      term: "twelve_month",
      editionLabel: "Explorer Ages 3–5",
      termLabel: "12 Months"
    }
  });

  const PLAN_KEY_BY_SELECTION = Object.freeze({
    elementary: {
      monthly: "monthly",
      six_month: "six_month",
      twelve_month: "twelve_month"
    },

    explorer: {
      monthly: "monthly_explorer",
      six_month: "six_month_explorer",
      twelve_month: "twelve_month_explorer"
    }
  });

  /* ==========================================================================
     DOM helpers
     ========================================================================== */

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];

  const planModal = $("#planModal");
  const planDialog = $(".plan-modal__dialog", planModal);
  const planSelector = $("#planSelector");
  const planContinueButton = $("#planModalContinue");
  const mobileCta = $("#mobileCta");
  const toast = $("#toast");
  const editionSelector = $("#editionSelector");
  const checkoutLoading = $("#checkoutLoading");

  let checkoutInProgress = false;

  /* ==========================================================================
     Attribution
     ========================================================================== */

  function setCheckoutLoading(isLoading) {
    if (!checkoutLoading) return;

    checkoutLoading.hidden = !isLoading;
  }

  function safeJsonParse(value, fallback = {}) {
    try {
      return JSON.parse(value) ?? fallback;
    } catch {
      return fallback;
    }
  }

  function getCurrentAttribution() {
    const params = new URLSearchParams(window.location.search);
    const attribution = {};

    ATTRIBUTION_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) attribution[key] = value;
    });

    return attribution;
  }

  function captureAttribution() {
    const current = getCurrentAttribution();

    if (!Object.keys(current).length) return;

    const firstTouch = safeJsonParse(localStorage.getItem(STORAGE_KEYS.firstTouch));

    if (!Object.keys(firstTouch).length) {
      localStorage.setItem(
        STORAGE_KEYS.firstTouch,
        JSON.stringify({
          ...current,
          captured_at: new Date().toISOString()
        })
      );
    }

    localStorage.setItem(
      STORAGE_KEYS.lastTouch,
      JSON.stringify({
        ...current,
        captured_at: new Date().toISOString()
      })
    );
  }

  function getSavedAttribution() {
    const lastTouch = safeJsonParse(localStorage.getItem(STORAGE_KEYS.lastTouch));
    const firstTouch = safeJsonParse(localStorage.getItem(STORAGE_KEYS.firstTouch));

    return {
      ...firstTouch,
      ...lastTouch
    };
  }

  /* ==========================================================================
     Analytics
     ========================================================================== */

  function pushDataLayer(eventName, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...payload
    });
  }

  function trackEvent(
    eventName,
    payload = {},
    { metaStandardEvent = null, ga4Event = null } = {}
  ) {
    const eventPayload = {
      ...payload,
      page_path: window.location.pathname,
      page_title: document.title
    };

    // Google Tag Manager / generic dataLayer
    pushDataLayer(eventName, eventPayload);

    // GA4, when gtag is installed
    if (typeof window.gtag === "function") {
      window.gtag("event", ga4Event || eventName, eventPayload);
    }

    // Meta Pixel, when fbq is installed
    if (typeof window.fbq === "function") {
      if (metaStandardEvent) {
        window.fbq("track", metaStandardEvent, eventPayload);
      } else {
        window.fbq("trackCustom", eventName, eventPayload);
      }
    }

    // Optional integration hook for other scripts
    window.dispatchEvent(
      new CustomEvent(`futureme:${eventName}`, {
        detail: eventPayload
      })
    );
  }

  function trackViewContent() {
    trackEvent(
      "future_me_view_content",
      {
        content_name: "Future Me Mail Club",
        content_category: "Mail Club Membership",
        value: 9.99,
        currency: "USD"
      },
      {
        metaStandardEvent: "ViewContent",
        ga4Event: "view_item"
      }
    );
  }

  /* ==========================================================================
     Shopify checkout URLs
     ========================================================================== */

  function getPlan(planKey) {
    return CONFIG.shopify.plans[planKey] || null;
  }

  function buildCartPermalink(plan) {
    const storeUrl = CONFIG.shopify.storeUrl.replace(/\/+$/, "");
    const variantId = String(plan.variantId || "").trim();

    if (!storeUrl || !variantId) return null;

    return `${storeUrl}/cart/${encodeURIComponent(variantId)}:1`;
  }

  function appendCheckoutParameters(rawUrl, planKey, placement) {
    let url;

    try {
      url = new URL(rawUrl, window.location.origin);
    } catch {
      return null;
    }

    const attribution = getSavedAttribution();

    ATTRIBUTION_KEYS.forEach((key) => {
      if (attribution[key] && !url.searchParams.has(key)) {
        url.searchParams.set(key, attribution[key]);
      }
    });

    if (!url.searchParams.has("ref")) {
      url.searchParams.set("ref", CONFIG.shopify.sourceRef);
    }

    url.searchParams.set("fm_plan", planKey);
    url.searchParams.set("fm_placement", placement || "unknown");

    const plan = getPlan(planKey);

    if (plan?.discountCode && !url.searchParams.has("discount")) {
      url.searchParams.set("discount", plan.discountCode);
    }

    return url.toString();
  }

  function resolveCheckoutUrl(planKey, placement = "unknown") {
    const plan = getPlan(planKey);

    if (!plan) return null;

    const directUrl = String(plan.checkoutUrl || "").trim();
    const fallbackUrl = buildCartPermalink(plan);
    const rawUrl = directUrl || fallbackUrl;

    if (!rawUrl) return null;

    return appendCheckoutParameters(rawUrl, planKey, placement);
  }

  async function startCheckout(
    planKey,
    placement = "unknown"
  ) {
    const plan = getPlan(planKey);

    if (!plan) {
      showToast(
        "That membership plan is not available."
      );
      return;
    }

    if (checkoutInProgress) return;
    checkoutInProgress = true;
    setCheckoutLoading(true);

    const checkoutUrl =
      String(plan.checkoutUrl || "").trim();

    const variantId =
      String(plan.variantId || "").trim();

    const sellingPlanId =
      String(plan.sellingPlanId || "").trim();

    const discountCode =
      String(plan.discountCode || "").trim();

    trackEvent(
      "future_me_initiate_checkout",
      {
        plan: planKey,
        plan_name: plan.name,
        placement,
        value: plan.price,
        currency: plan.currency || "USD"
      },
      {
        metaStandardEvent: "InitiateCheckout",
        ga4Event: "begin_checkout"
      }
    );

    try {

      /* ==========================================
        OPTION 1:
        Use a manually supplied checkout URL
        ========================================== */

      if (checkoutUrl) {

        const finalUrl =
          typeof appendCheckoutParameters === "function"
            ? appendCheckoutParameters(
                checkoutUrl,
                planKey,
                placement
              )
            : checkoutUrl;

        window.location.assign(finalUrl);

        return;
      }


      /* ==========================================
        OPTION 2:
        Create a fresh Shopify cart
        ========================================== */

      if (!variantId) {
        throw new Error(
          `Add either checkoutUrl or variantId for "${plan.name}".`
        );
      }


      /* Get the real Shopify .myshopify.com domain */

      const shopDomain =
        window.FutureMeShopifyConfig?.shopDomain ||
        window.Shopify?.shop;

      if (!shopDomain) {
        throw new Error(
          "Could not determine the Shopify store domain."
        );
      }


      /* Build the official Storefront API endpoint */

      const storefrontApiUrl =
        `https://${shopDomain}/api/2026-07/graphql.json`;

      console.log(
        "Shopify Storefront API URL:",
        storefrontApiUrl
      );


      /* Build the cart line */

      const line = {
        merchandiseId:
          `gid://shopify/ProductVariant/${variantId}`,

        quantity: 1
      };


      /* Add selling plan when applicable */

      if (sellingPlanId) {
        line.sellingPlanId =
          `gid://shopify/SellingPlan/${sellingPlanId}`;
      }


      const input = {
        lines: [line]
      };


      /* Optional discount */

      if (discountCode) {
        input.discountCodes = [
          discountCode
        ];
      }


      /* Shopify GraphQL mutation */

      const query = `
        mutation cartCreate(
          $input: CartInput
        ) {

          cartCreate(
            input: $input
          ) {

            cart {
              id
              checkoutUrl
            }

            userErrors {
              field
              message
            }

            warnings {
              code
              message
            }
          }
        }
      `;


      /* Make Storefront API request */

      const response =
        await fetch(
          storefrontApiUrl,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              "Accept":
                "application/json"
            },

            body: JSON.stringify({
              query,

              variables: {
                input
              }
            })
          }
        );


      /* Handle HTTP errors */

      if (!response.ok) {

        const responseText =
          await response.text();

        console.error(
          "Shopify HTTP error:",
          response.status,
          responseText
        );

        throw new Error(
          `Shopify returned HTTP ${response.status}.`
        );
      }


      /* Parse Shopify response */

      const result =
        await response.json();

      console.log(
        "Shopify cartCreate result:",
        result
      );


      /* GraphQL-level errors */

      if (
        Array.isArray(result.errors) &&
        result.errors.length
      ) {

        console.error(
          "Shopify GraphQL errors:",
          result.errors
        );

        throw new Error(
          result.errors[0]?.message ||
          "Unable to create checkout."
        );
      }


      const cartCreate =
        result.data?.cartCreate;


      /* Cart validation errors */

      if (
        cartCreate?.userErrors?.length
      ) {

        console.error(
          "Shopify cart errors:",
          cartCreate.userErrors
        );

        throw new Error(
          cartCreate
            .userErrors[0]
            .message
        );
      }


      /* Log warnings if any */

      if (
        cartCreate?.warnings?.length
      ) {

        console.warn(
          "Shopify cart warnings:",
          cartCreate.warnings
        );
      }


      /* Get fresh checkout URL */

      const newCheckoutUrl =
        cartCreate
          ?.cart
          ?.checkoutUrl;


      if (!newCheckoutUrl) {

        throw new Error(
          "Shopify did not return a checkout URL."
        );
      }


      /* Go directly to checkout */

      window.location.assign(
        newCheckoutUrl
      );


    } catch (error) {

      checkoutInProgress = false;
      setCheckoutLoading(false);

      console.error(
        `Future Me checkout failed for ${planKey}:`,
        error
      );

      showToast(
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again."
      );
    }
  }

  /* ==========================================================================
     Plan selector modal
     ========================================================================== */

  function getFocusableElements() {
    if (!planDialog) return [];

    return $$(
      'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      planDialog
    ).filter((element) => !element.hasAttribute("hidden"));
  }

  function syncPlanModalUi(planKey) {
    const selection = PLAN_SELECTIONS[planKey];

    if (!selection) return;

    if (editionSelector) {
      $$("[data-edition-option]", editionSelector).forEach((button) => {
        const isSelected =
          button.dataset.editionOption === selection.edition;

        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-pressed", String(isSelected));
      });
    }

    if (planSelector) {
      $$("[data-plan-group]", planSelector).forEach((group) => {
        group.hidden =
          group.dataset.planGroup !== selection.edition;
      });
    }
  }

  function setSelectedPlan(planKey, { track = true } = {}) {
    const plan = getPlan(planKey);
    const selection = PLAN_SELECTIONS[planKey];

    if (!plan || !selection) return;

    state.selectedPlan = planKey;

    syncPlanModalUi(planKey);

    if (planSelector) {
      $$("[data-plan-option]", planSelector).forEach((option) => {
        const isSelected =
          option.dataset.planOption === planKey;

        const radio =
          $('input[type="radio"]', option);

        option.classList.toggle("is-selected", isSelected);

        if (radio) {
          radio.checked = isSelected;
        }
      });
    }

    if (planContinueButton) {
      const formattedPrice =
        Number.isInteger(plan.price)
          ? `$${plan.price}`
          : `$${plan.price.toFixed(2)}`;

      planContinueButton.textContent =
        `Continue: ${selection.editionLabel} • ` +
        `${selection.termLabel} • ${formattedPrice}`;
    }

    if (track) {
      trackEvent("future_me_plan_selected", {
        plan: planKey,
        plan_name: plan.name,
        edition: selection.edition,
        edition_label: selection.editionLabel,
        membership_term: selection.term,
        modal_source: state.modalSource,
        value: plan.price,
        currency: plan.currency
      });
    }
  }

  // function setSelectedPlan(planKey, { track = true } = {}) {
  //   const plan = getPlan(planKey);

  //   if (!plan) return;

  //   state.selectedPlan = planKey;

  //   $$("[data-plan-option]", planSelector).forEach((option) => {
  //     const isSelected = option.dataset.planOption === planKey;
  //     const radio = $('input[type="radio"]', option);

  //     option.classList.toggle("is-selected", isSelected);

  //     if (radio) {
  //       radio.checked = isSelected;
  //     }
  //   });

  //   if (planContinueButton) {
  //     const buttonLabels = {
  //       monthly: `Continue Monthly — $${plan.price.toFixed(2)}`,
  //       six_month: `Continue with 6 Months — $${plan.price}`,
  //       twelve_month: `Continue with 12 Months — $${plan.price}`
  //     };

  //     planContinueButton.textContent = buttonLabels[planKey] || "Continue to Checkout";
  //   }

  //   if (track) {
  //     trackEvent("future_me_plan_selected", {
  //       plan: planKey,
  //       plan_name: plan.name,
  //       modal_source: state.modalSource,
  //       value: plan.price,
  //       currency: plan.currency
  //     });
  //   }
  // }

  function openPlanModal(defaultPlan = CONFIG.planSelector.defaultPlan, source = "unknown") {
    if (!planModal || !planDialog) return;

    state.lastFocusedElement = document.activeElement;
    state.modalSource = source;

    setSelectedPlan(defaultPlan, { track: false });

    planModal.hidden = false;
    document.body.classList.add("modal-open");

    window.requestAnimationFrame(() => {
      planModal.classList.add("is-open");
      planDialog.focus();
    });

    trackEvent("future_me_plan_selector_opened", {
      source,
      default_plan: defaultPlan
    });
  }

  function closePlanModal({ restoreFocus = true } = {}) {
    if (!planModal || planModal.hidden) return;

    planModal.classList.remove("is-open");
    document.body.classList.remove("modal-open");

    window.setTimeout(() => {
      planModal.hidden = true;

      if (
        restoreFocus &&
        state.lastFocusedElement &&
        typeof state.lastFocusedElement.focus === "function"
      ) {
        state.lastFocusedElement.focus();
      }
    }, 180);
  }

  function handleModalKeydown(event) {
    if (!planModal || planModal.hidden) return;

    if (event.key === "Escape") {
      event.preventDefault();
      closePlanModal();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = getFocusableElements();

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function setupPlanModal() {
    $$(".js-open-plan-modal").forEach((button) => {
      button.addEventListener("click", () => {
        openPlanModal(
          button.dataset.defaultPlan || CONFIG.planSelector.defaultPlan,
          button.dataset.placement || "unknown"
        );
      });
    });

    $$("[data-modal-close]", planModal).forEach((element) => {
      element.addEventListener("click", () => closePlanModal());
    });

    if (editionSelector) {
      $$("[data-edition-option]", editionSelector).forEach((button) => {
        button.addEventListener("click", () => {
          const nextEdition =
            button.dataset.editionOption;

          const currentSelection =
            PLAN_SELECTIONS[state.selectedPlan] ||
            PLAN_SELECTIONS[CONFIG.planSelector.defaultPlan];

          const nextPlanKey =
            PLAN_KEY_BY_SELECTION[nextEdition]?.[
              currentSelection.term
            ];

          if (nextPlanKey) {
            setSelectedPlan(nextPlanKey);
          }
        });
      });
    }
    
    $$("[data-plan-option]", planSelector).forEach((option) => {
      const radio = $('input[type="radio"]', option);

      option.addEventListener("click", () => {
        setSelectedPlan(option.dataset.planOption);
      });

      radio?.addEventListener("change", () => {
        if (radio.checked) {
          setSelectedPlan(radio.value);
        }
      });
    });

    planContinueButton?.addEventListener("click", () => {
      const selectedPlan = state.selectedPlan;
      const placement = `plan-modal:${state.modalSource}`;

      closePlanModal({ restoreFocus: false });
      startCheckout(selectedPlan, placement);
    });

    $("[data-scroll-to-pricing]", planModal)?.addEventListener("click", () => {
      window.setTimeout(() => {
        $("#pricing")?.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }, 200);
    });

    document.addEventListener("keydown", handleModalKeydown);
  }

  /* ==========================================================================
     Direct checkout buttons
     ========================================================================== */

  function setupCheckoutButtons() {
    $$(".js-checkout").forEach((button) => {
      button.addEventListener("click", () => {
        const planKey = button.dataset.plan || CONFIG.planSelector.defaultPlan;
        const placement = button.dataset.placement || "unknown";

        trackEvent("future_me_plan_cta_clicked", {
          plan: planKey,
          placement
        });

        startCheckout(planKey, placement);
      });
    });
  }

  /* ==========================================================================
     Countdown
     ========================================================================== */

  function updateCountdown() {
    const countdown = $("#countdown");

    if (!countdown) return;

    const target = new Date(CONFIG.firstMailDate).getTime();
    const now = Date.now();
    const distance = Math.max(0, target - now);

    if (!Number.isFinite(target)) {
      countdown.hidden = true;
      return;
    }

    if (distance === 0) {
      countdown.innerHTML =
        '<div class="countdown-live"><strong>The Founding Issue is mailing now!</strong></div>';
      return;
    }

    const values = {
      days: Math.floor(distance / (1000 * 60 * 60 * 24)),
      hours: Math.floor((distance / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((distance / (1000 * 60)) % 60),
      seconds: Math.floor((distance / 1000) % 60)
    };

    Object.entries(values).forEach(([id, value]) => {
      const element = document.getElementById(id);

      if (element) {
        element.textContent = String(value).padStart(2, "0");
      }
    });
  }

  function setupCountdown() {
    updateCountdown();
    window.setInterval(updateCountdown, 1000);
  }

  /* ==========================================================================
     FAQ
     ========================================================================== */

  function setupFaq() {
    $$(".faq-question").forEach((button) => {
      button.addEventListener("click", () => {
        const answerId = button.getAttribute("aria-controls");
        const answer = answerId ? document.getElementById(answerId) : null;
        const isOpen = button.getAttribute("aria-expanded") === "true";

        button.setAttribute("aria-expanded", String(!isOpen));

        if (answer) {
          answer.hidden = isOpen;
        }
      });
    });
  }

  /* ==========================================================================
     Sticky mobile CTA
     ========================================================================== */

  function updateMobileCta() {
    if (!mobileCta) return;

    const shouldShow =
      window.scrollY > CONFIG.mobileCta.showAfterPx &&
      !state.pricingInView &&
      !document.body.classList.contains("modal-open");

    mobileCta.classList.toggle("is-visible", shouldShow);
    mobileCta.setAttribute("aria-hidden", String(!shouldShow));
  }

  function setupMobileCta() {
    if (!mobileCta) return;

    window.addEventListener("scroll", updateMobileCta, { passive: true });

    const pricingSection = $("#pricing");

    if ("IntersectionObserver" in window && pricingSection) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          state.pricingInView = entry.isIntersecting;
          updateMobileCta();
        },
        { threshold: 0.2 }
      );

      observer.observe(pricingSection);
    }

    updateMobileCta();
  }

  /* ==========================================================================
     Engagement tracking
     ========================================================================== */

  function setupSectionTracking() {
    if (!("IntersectionObserver" in window)) return;

    const tracked = new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || tracked.has(entry.target)) return;

          tracked.add(entry.target);

          trackEvent("future_me_section_viewed", {
            section: entry.target.id || entry.target.dataset.trackSection
          });
        });
      },
      { threshold: 0.35 }
    );

    [
      $("#inside"),
      $("#books"),
      $("#roadmap"),
      $("#pricing"),
      $("#faq")
    ]
      .filter(Boolean)
      .forEach((section) => observer.observe(section));
  }

  /* ==========================================================================
     Toast
     ========================================================================== */

  function showToast(message) {
    if (!toast) return;

    toast.textContent = message;
    toast.classList.add("is-visible");

    window.clearTimeout(state.toastTimer);

    state.toastTimer = window.setTimeout(() => {
      toast.classList.remove("is-visible");
    }, 4500);
  }

  /* ==========================================================================
     Init
     ========================================================================== */

  function init() {
    captureAttribution();
    setupPlanModal();
    setupCheckoutButtons();
    setupCountdown();
    setupFaq();
    setupMobileCta();
    setupSectionTracking();
    trackViewContent();

    // Expose a small, intentional API for debugging or future integrations.
    window.FutureMeMailClub = Object.freeze({
      openPlanModal,
      startCheckout,
      trackEvent,
      config: CONFIG
    });
  }

  /* ==========================================================================
   Checkout loading reset
   ========================================================================== */

  function resetCheckoutLoading() {
    checkoutInProgress = false;
    setCheckoutLoading(false);
  }

  window.addEventListener("pagehide", resetCheckoutLoading);

  window.addEventListener("pageshow", resetCheckoutLoading);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();

